import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { onWOChanged } from "@/lib/woEvents";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, CalendarDays, RefreshCw, Plus, Search, X, Clock, UserRound, MapPin } from "lucide-react";
import CreateWODialog from "@/components/CreateWODialog";
import { ArmadaSlotStack } from "@/components/ArmadaSlotStack";
import { BreakLeaveBar } from "@/components/BreakLeaveBar";
import { toast } from "sonner";

const HOUR_START = 7;
const HOUR_END = 21;
const HOUR_ROWS = HOUR_END - HOUR_START;   // 14 rows
const ROW_H = 64;                          // px per hour
const MIN_COL_W = 140;                     // px min per technician column (columns auto-expand via 1fr)
const GUTTER = 64;                         // hour label column
const SNAP_MIN = 15;                       // drag/resize snap = 15 menit
const PX_PER_MIN = ROW_H / 60;
const MIN_DURATION_MIN = 15;               // minimal panjang WO setelah resize

function pxToSnappedMin(dy) {
  return Math.round(dy / PX_PER_MIN / SNAP_MIN) * SNAP_MIN;
}

function shiftIso(iso, deltaMin) {
  // Shift jam/menit di ISO string dgn deltaMin, TIDAK mengubah timezone offset & tanggal.
  if (!iso || iso.length < 19) return iso;
  const datePart = iso.slice(0, 10);
  const h = parseInt(iso.slice(11, 13), 10);
  const m = parseInt(iso.slice(14, 16), 10);
  const secTz = iso.slice(16);   // ":ss+08:00"
  let totalMin = h * 60 + m + deltaMin;
  totalMin = Math.max(0, Math.min(24*60 - 1, totalMin));
  const nh = Math.floor(totalMin / 60);
  const nm = totalMin % 60;
  return `${datePart}T${String(nh).padStart(2,"0")}:${String(nm).padStart(2,"0")}${secTz}`;
}

const STATUS_STYLE = {
  NEW:          { bg: "#DBEAFE", border: "#3B82F6", text: "#1E3A8A" },
  SCHEDULED:    { bg: "#FEF3C7", border: "#F59E0B", text: "#78350F" },
  ASSIGNED:     { bg: "#EDE9FE", border: "#7C3AED", text: "#4C1D95" },
  IN_PROGRESS: { bg: "#DBEAFE", border: "#2563EB", text: "#1E3A8A" },
  DONE:         { bg: "#D1FAE5", border: "#059669", text: "#064E3B" },
  CANCELLED:    { bg: "#FEE2E2", border: "#DC2626", text: "#7F1D1D" },
};

function pad(n){ return String(n).padStart(2,"0"); }
function shiftDay(iso, dir) {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + dir);
  return d.toISOString().slice(0,10);
}
function fmtDayID(iso) {
  const d = new Date(iso + "T00:00:00Z");
  const days = ["Minggu","Senin","Selasa","Rabu","Kamis","Jumat","Sabtu"];
  const months = ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"];
  return `${days[d.getUTCDay()]}, ${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
function hoursOf(iso) {
  // Backend stores scheduled_start dalam WITA offset (+08:00). Ambil jam dari
  // string ISO langsung (posisi 11..16) supaya display sesuai wall-clock WITA
  // apa pun timezone user/browser.
  if (!iso || iso.length < 16) return 0;
  const h = parseInt(iso.slice(11, 13), 10);
  const m = parseInt(iso.slice(14, 16), 10);
  return h + m / 60;
}

// Today in WITA (UTC+8) → yyyy-mm-dd
function todayWita() {
  const d = new Date(Date.now() + 8 * 60 * 60 * 1000);
  return d.toISOString().slice(0, 10);
}

export default function CalendarBoard() {
  const [day, setDay] = useState(() => todayWita());
  // v84.41 — Search state (customer name / phone / address fragment)
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dlg, setDlg] = useState({ open: false, mode: "create", initial: null });
  // Drag/resize state: {id, mode:'move'|'resize', startY, deltaMin, origStart, origEnd}
  const [drag, setDrag] = useState(null);
  // v84.50 — Personnel cache + break/cuti + armada assignments (lifted ke parent
  // supaya SATU call listArmadaAssignments untuk seluruh armada, bukan N-call per
  // armada). Slot child sekarang consume dari `assignmentsByArmada` map di sini.
  const [personnel, setPersonnel] = useState([]);
  const [breakLeaves, setBreakLeaves] = useState([]);
  const [assignmentsByArmada, setAssignmentsByArmada] = useState({});   // armadaId → assignment doc
  const [slotReloadKey, setSlotReloadKey] = useState(0);

  // v84.99 — Set of tech first-names (lowercase) untuk validasi badge "minta X".
  // Diambil dari personnel (bukan technicians/armada) supaya cover teknisi individual
  // spt Lukman, Hendro, Andi, dll — bukan nama armada spt GA-01.
  const techNameSet = useMemo(() => {
    const set = new Set();
    for (const p of personnel) {
      const name = String(p?.name || "").trim();
      if (!name) continue;
      // Ambil semua kata >=3 char (skip "Pak", "Bu", "Bpk")
      for (const w of name.split(/\s+/)) {
        const lw = w.toLowerCase().replace(/[^a-z]/g, "");
        if (lw.length >= 3 && !["pak","bpk","bapak","ibu","kak","mas","mba","the"].includes(lw)) {
          set.add(lw);
        }
      }
    }
    return set;
  }, [personnel]);

  // Load personnel sekali (cache di parent)
  useEffect(() => {
    (async () => {
      try { setPersonnel(await api.listPersonnel()); }
      catch (e) { console.error("[Cal] personnel load fail", e); }
    })();
  }, []);

  // v84.50 — Load semua armada_assignments utk `day` dalam SATU call, group by armada_id.
  // Sebelumnya: N call (satu per armada) dari dalam ArmadaSlotStack — n+1 problem.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.listArmadaAssignments({ date_from: day, date_to: day });
        if (cancelled) return;
        const map = {};
        for (const row of (r.rows || [])) map[row.armada_id] = row;
        setAssignmentsByArmada(map);
      } catch (e) {
        if (!cancelled) setAssignmentsByArmada({});
      }
    })();
    return () => { cancelled = true; };
  }, [day, slotReloadKey]);

  // Load break/cuti list untuk `day` — dipakai BreakLeaveBar + di-passthrough ke ArmadaSlotStack
  // supaya slot personel yg lagi cuti bisa ditandai visual (line-through merah).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.listBreakLeaves({ date: day });
        if (!cancelled) setBreakLeaves(r.rows || []);
      } catch (e) { if (!cancelled) setBreakLeaves([]); }
    })();
    return () => { cancelled = true; };
  }, [day, slotReloadKey]);

  // Urutan request: respons load() lama / yg mulai sebelum perubahan lokal diabaikan.
  const loadSeqRef = useRef(0);
  async function load() {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    try {
      const r = await api.schedule(day);
      if (seq === loadSeqRef.current) setData(r);
    } finally { if (seq === loadSeqRef.current) setLoading(false); }
  }
  // v84.41 — Live search WO by phone / name / address fragment
  useEffect(() => {
    if (!searchQ || searchQ.trim().length < 3) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    const t = setTimeout(async () => {
      setSearchBusy(true);
      try {
        const r = await api.searchOrders(searchQ.trim());
        setSearchResults(r || []);
        setSearchOpen(true);
      } catch { setSearchResults([]); }
      finally { setSearchBusy(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [searchQ]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [day]);

  // v84.31 — Subscribe global WO event supaya kalender realtime refresh saat
  // admin klik "Buat WO" di halaman /work-orders (atau vice versa dari dialog).
  useEffect(() => {
    const off = onWOChanged((detail) => {
      // Cek apakah WO menyentuh hari yg sedang ditampilkan; kalau ya, reload.
      // Kalau info tanggal tidak tersedia (mis. delete), tetap reload — biaya kecil.
      const wDay = detail?.wo?.scheduled_start?.slice(0, 10);
      if (!wDay || wDay === day) {
        load();
      }
    });
    return off;
  }, [day]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Drag lateral: pindah WO ke kolom armada lain (+ geser jam bila ikut digeser).
  async function moveToTech(d) {
    loadSeqRef.current++;
    const snapshot = d.snapshot;
    const newStart = d.deltaMin ? shiftIso(d.origStart, d.deltaMin) : d.origStart;
    const newEnd   = d.deltaMin ? shiftIso(d.origEnd, d.deltaMin) : d.origEnd;
    setData(prev => {
      if (!prev?.rows) return prev;
      const moved = prev.rows.flatMap(r => r.orders).find(o => o.id === d.id);
      if (!moved) return prev;
      const upd = { ...moved, technician_id: d.targetTechId, scheduled_start: newStart, scheduled_end: newEnd };
      return {
        ...prev,
        rows: prev.rows.map(r => {
          const orders = r.orders.filter(o => o.id !== d.id);
          return { ...r, orders: r.technician.id === d.targetTechId ? [...orders, upd] : orders };
        }),
      };
    });
    try {
      const payload = { technician_id: d.targetTechId };
      if (d.deltaMin) { payload.scheduled_start = newStart; payload.scheduled_end = newEnd; }
      await api.updateOrder(d.id, payload);
      load();   // refresh data terbaru dari server
    } catch (err) {
      setData(snapshot);   // rollback ke kondisi sebelum drag
      alert("Gagal memindahkan WO. Silakan coba lagi.");
    }
  }

  // Attach global mousemove/mouseup listeners saat drag aktif.
  useEffect(() => {
    if (!drag) return;
    function onMove(e) {
      const dy = e.clientY - drag.startY;
      let dx = 0, targetTechId = drag.techId;
      if (drag.mode === "move") {
        // Pindah armada (lateral): cari kolom armada di bawah kursor
        dx = e.clientX - drag.startX;
        const cell = document.elementsFromPoint(e.clientX, e.clientY).find(el => el.dataset?.colTech);
        if (cell) targetTechId = cell.dataset.colTech;
      }
      setDrag(d => d ? { ...d, deltaMin: pxToSnappedMin(dy), dx, targetTechId } : d);
    }
    async function onUp() {
      // Snapshot dulu sebelum reset
      const d = drag;
      setDrag(null);
      if (!d) return;
      const techChanged = d.mode === "move" && d.targetTechId && d.targetTechId !== d.techId;
      // Klik biasa (tanpa drag) → buka dialog edit
      if (d.deltaMin === 0 && !techChanged) {
        d.onSimpleClick?.();
        return;
      }
      if (techChanged) {
        await moveToTech(d);
        return;
      }
      let newStart = d.origStart, newEnd = d.origEnd;
      if (d.mode === "move") {
        newStart = shiftIso(d.origStart, d.deltaMin);
        newEnd   = shiftIso(d.origEnd,   d.deltaMin);
      } else if (d.mode === "resize") {
        newEnd = shiftIso(d.origEnd, d.deltaMin);
        // Pastikan minimal duration
        if ((hoursOf(newEnd) - hoursOf(d.origStart)) * 60 < MIN_DURATION_MIN) {
          return;
        }
      }
      loadSeqRef.current++;   // respons load() yg sedang jalan sudah basi
      // v84.51 — Optimistic UI: update local state DULU sebelum PATCH ke server.
      // Kalau server error → rollback via load(). Tidak ada toast utk hindari delay.
      setData(prev => {
        if (!prev?.rows) return prev;
        return {
          ...prev,
          rows: prev.rows.map(r => ({
            ...r,
            orders: r.orders.map(o =>
              o.id === d.id ? { ...o, scheduled_start: newStart, scheduled_end: newEnd } : o
            ),
          })),
        };
      });
      try {
        await api.updateOrder(d.id, { scheduled_start: newStart, scheduled_end: newEnd });
        // Sukses — state lokal sudah benar, tidak perlu reload (hemat 6 detik).
      } catch (err) {
        toast.error("Gagal menyimpan perubahan jadwal — rollback");
        load();   // Rollback ke state server saat gagal
      }
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [drag]);

  const rows = data?.rows || [];
  const techs = rows.map(r => r.technician);
  const minInnerW = GUTTER + Math.max(1, rows.length) * MIN_COL_W;
  const gridCols = `${GUTTER}px repeat(${rows.length}, minmax(${MIN_COL_W}px, 1fr))`;

  function openCreate(techId=null, hour=8) {
    setDlg({ open: true, mode: "create", initial: { day, hour, technician_id: techId } });
  }
  function openEdit(order) {
    setDlg({ open: true, mode: "edit", initial: order });
  }

  const stats = useMemo(() => {
    const all = rows.flatMap(r => r.orders);
    return {
      total: all.length,
      scheduled: all.filter(o => o.status === "SCHEDULED").length,
      in_progress: all.filter(o => o.status === "IN_PROGRESS").length,
      done: all.filter(o => o.status === "DONE").length,
      cancelled: all.filter(o => o.status === "CANCELLED").length,
    };
  }, [rows]);

  return (
    <div className="border border-border bg-white rounded-sm overflow-hidden" data-testid="calendar-board">
      {/* Top control bar */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-white">
        <CalendarDays className="w-4 h-4 text-[#E65100]" />
        <div className="font-['Manrope'] font-bold text-[15px] leading-none">Kalender Harian · 9 Tim</div>
        <div className="ml-2 hidden md:flex items-center gap-1">
          <Button size="icon" variant="outline" className="h-8 w-8 rounded-sm" onClick={()=>setDay(shiftDay(day,-1))} data-testid="prev-day-btn">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" className="h-8 rounded-sm text-[12px]" onClick={()=>setDay(todayWita())} data-testid="today-btn">Hari ini</Button>
          <Button size="icon" variant="outline" className="h-8 w-8 rounded-sm" onClick={()=>setDay(shiftDay(day,+1))} data-testid="next-day-btn">
            <ChevronRight className="w-4 h-4" />
          </Button>
          {/* v85.40 — Date picker: lompat cepat ke tanggal mana saja */}
          <input
            type="date"
            value={day}
            onChange={e => { if (e.target.value) setDay(e.target.value); }}
            className="h-8 ml-1 px-2 rounded-sm bg-background border border-border text-[12px] outline-none focus:border-[#E65100] cursor-pointer"
            title="Pilih tanggal untuk lompat ke jadwal yang diinginkan"
            data-testid="cal-date-picker"
          />
        </div>
        <div className="text-[13px] font-semibold ml-1">{fmtDayID(day)}</div>
        {/* v84.41 — Search input: phone / name / alamat */}
        <div className="ml-4 relative w-[300px]">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            value={searchQ}
            onChange={e => setSearchQ(e.target.value)}
            onFocus={() => searchResults.length && setSearchOpen(true)}
            placeholder="Cari WO (no HP / nama / alamat)…"
            className="w-full h-8 pl-7 pr-7 rounded-sm bg-background border border-border text-[12px] outline-none focus:border-[#E65100]"
            data-testid="cal-search-input"
          />
          {searchQ && (
            <button onClick={() => { setSearchQ(""); setSearchOpen(false); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
              data-testid="cal-search-clear">
              <X className="w-3 h-3" />
            </button>
          )}
          {searchOpen && (
            <div className="absolute top-full left-0 mt-1 w-[560px] max-h-[400px] overflow-y-auto bg-card border border-border rounded-sm shadow-2xl z-50"
              data-testid="cal-search-results">
              {searchBusy && <div className="p-3 text-[11px] text-zinc-500">Mencari…</div>}
              {!searchBusy && searchResults.length === 0 && (
                <div className="p-3 text-[11px] text-zinc-500">Tidak ada hasil untuk "{searchQ}"</div>
              )}
              {!searchBusy && searchResults.map(wo => (
                <button key={wo.id}
                  onClick={() => {
                    setDay(wo.scheduled_start ? wo.scheduled_start.slice(0, 10) : todayWita());
                    setSearchOpen(false);
                    setTimeout(() => openEdit(wo), 400);
                  }}
                  className="w-full text-left px-3 py-2 border-b border-border hover:bg-white/5 last:border-b-0"
                  data-testid={`cal-search-result-${wo.id}`}>
                  <div className="flex items-baseline gap-2">
                    <span className="text-[10px] font-mono text-zinc-500 shrink-0">
                      {wo.scheduled_start ? wo.scheduled_start.slice(0, 10) : "—"}
                    </span>
                    <span className="text-[10px] font-mono text-zinc-400 shrink-0">
                      {wo.scheduled_start ? wo.scheduled_start.slice(11, 16) : ""}
                    </span>
                    <span className="text-[12px] font-semibold truncate">
                      {(wo.service_type || "SERVICE").toUpperCase()} · {wo.quantity || 1} unit
                    </span>
                    {wo.status && (
                      <span className="ml-auto text-[9px] font-bold uppercase text-zinc-400">
                        {wo.status}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-400 truncate mt-0.5 flex items-center gap-1.5">
                    <span className="truncate">{wo.customer_display_name || wo.customer_name} · {wo.customer_phone}</span>
                    {wo.technician_name && (
                      <span className="ml-auto shrink-0 px-1.5 py-0.5 rounded-sm bg-blue-900/40 text-blue-200 border border-blue-700 text-[9px] font-semibold uppercase tracking-wider"
                            data-testid={`cal-search-result-tech-${wo.id}`}>
                        {wo.technician_name}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    {wo.address}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="ml-auto flex items-center gap-3 text-[11px]">
          {/* v84.49 — Break/Cuti register: visibility ketersediaan tenaga kerja hari ini */}
          <BreakLeaveBar
            date={day}
            personnel={personnel}
            reloadKey={slotReloadKey}
            onChanged={() => setSlotReloadKey(k => k + 1)}
          />
          <Legend color="#F59E0B" label={`Scheduled ${stats.scheduled}`} />
          <Legend color="#2563EB" label={`AI-created`} />
          <Legend color="#059669" label={`Done ${stats.done}`} />
          <Legend color="#DC2626" label={`Cancel ${stats.cancelled}`} />
          <Button size="sm" className="bg-[#E65100] hover:bg-[#F57C00] rounded-sm gap-1.5 h-8" onClick={()=>openCreate(null, 8)} data-testid="cal-new-wo-btn">
            <Plus className="w-3.5 h-3.5" /> Work Order
          </Button>
          {/* v84.47 — Sync Google button removed. GCal sync is now one-way (App → GCal). */}
          <Button size="sm" variant="outline" className="rounded-sm gap-1.5 h-8" onClick={load} data-testid="cal-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${loading?'animate-spin':''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Scroll container — v84.51 tinggikan supaya jam 08-19 masuk semua.
          Header non-sticky supaya bisa scroll keluar view utk kasih ruang lebih. */}
      <div className="overflow-auto" style={{maxHeight: "calc(100vh - 150px)"}}>
        <div style={{minWidth: minInnerW, width: "100%"}}>
          {/* Header row: technicians (non-sticky, scrollable) */}
          <div className="grid bg-card border-b border-border"
               style={{gridTemplateColumns: gridCols}}>
            <div className="border-r border-border" />
            {rows.map(r => (
              <div key={r.technician.id} className="border-r border-border px-3 py-2 text-center">
                {/* v84.136 — Custom tooltip nama polygon Google My Maps (dinamis) */}
                <div className="relative mx-auto mb-1 w-8 h-8 group">
                  <div
                    className="w-8 h-8 rounded-full grid place-items-center text-white text-[11px] font-bold cursor-help"
                    style={{background: r.technician.color}}
                    data-testid={`tech-avatar-${r.technician.id}`}
                  >
                    {r.technician.name.split(" ").map(x=>x[0]).slice(0,2).join("")}
                  </div>
                  <div
                    className="pointer-events-none absolute left-1/2 top-full -translate-x-1/2 mt-1 z-50 hidden group-hover:block"
                    role="tooltip"
                    data-testid={`tech-zone-tooltip-${r.technician.id}`}
                  >
                    <div className="w-[220px] max-w-[220px] bg-zinc-900 border border-zinc-700 text-white text-[11px] leading-snug rounded-md px-2.5 py-2 shadow-lg whitespace-normal break-words text-left">
                      {r.technician.zone_name ? (
                        <>
                          <span className="text-zinc-400 font-semibold">Zona:</span>{" "}
                          <span className="font-medium">{r.technician.zone_name}</span>
                        </>
                      ) : (
                        <span className="text-zinc-400 italic">
                          Belum ada zona (assign di menu Tim Armada)
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-[12px] font-semibold truncate leading-tight">{r.technician.name}</div>
                <div className="text-[10px] text-zinc-500 truncate">{r.technician.base_address}</div>
                <div className="text-[9px] text-zinc-400 font-mono uppercase mt-0.5">{r.orders.length} order</div>
                {/* v84.49 — 3 slot personel per armada (terhubung ke commission dashboard) */}
                <ArmadaSlotStack
                  date={day}
                  armadaId={r.technician.id}
                  personnel={personnel}
                  breakLeaves={breakLeaves}
                  assignment={assignmentsByArmada[r.technician.id]}
                  reloadKey={slotReloadKey}
                  onChanged={() => setSlotReloadKey(k => k + 1)}
                />
              </div>
            ))}
            {rows.length === 0 && (
              <div className="px-4 py-6 text-center text-zinc-500 text-[13px]">Belum ada teknisi. Buka menu <b>Teknisi</b> → Seed 9 Contoh.</div>
            )}
          </div>

          {/* Body grid */}
          <div className="relative grid"
               style={{
                 gridTemplateColumns: gridCols,
                 gridAutoRows: `${ROW_H}px`,
                 gridTemplateRows: `repeat(${HOUR_ROWS}, ${ROW_H}px)`,
               }}>
            {/* Hour labels + row lines — sticky left utk keep visible saat horizontal scroll */}
            {Array.from({length: HOUR_ROWS}).map((_, i) => (
              <div key={`h-${i}`}
                   className="border-b border-r border-border bg-card text-[10px] text-zinc-500 font-mono text-right pr-2 pt-1 sticky left-0 z-10"
                   style={{gridColumn: 1, gridRow: i+1}}>
                {pad(HOUR_START + i)}:00
              </div>
            ))}

            {/* Column cells (grid lines) — click to create WO */}
            {rows.map((r, colIdx) =>
              Array.from({length: HOUR_ROWS}).map((_, i) => (
                <div key={`c-${r.technician.id}-${i}`}
                     onClick={()=>openCreate(r.technician.id, HOUR_START + i)}
                     className="border-b border-r border-border bg-card hover:bg-muted/60 transition-colors cursor-pointer"
                     data-testid={`cal-slot-${r.technician.id}-${i}`}
                     data-col-tech={r.technician.id}
                     style={{gridColumn: colIdx + 2, gridRow: i+1,
                             ...(drag?.mode === "move" && drag.targetTechId === r.technician.id && drag.targetTechId !== drag.techId
                                 ? { backgroundColor: "rgba(255,122,26,0.14)" } : {})}} />
              ))
            )}

            {/* Now indicator */}
            <NowLine />

            {/* Events — v84.51: pass overlap layout supaya event bertindih mengecil horizontal */}
            {rows.map((r, colIdx) => {
              const layout = computeOverlapLayout(r.orders);
              return r.orders.map(o => (
                <EventCard key={o.id} order={o} tech={r.technician} colIdx={colIdx}
                           overlap={layout.get(o.id)}
                           techNameSet={techNameSet}
                           onClick={()=>openEdit(o)}
                           drag={drag} setDrag={(v) => setDrag(typeof v === "function" ? v : (v && { ...v, snapshot: data }))} />
              ));
            })}
          </div>
        </div>
      </div>

      {/* v84.40 — CreateWODialog dipakai untuk BOTH create + edit. Form sama identik. */}
      <CreateWODialog
        open={dlg.open && dlg.mode === "create"}
        onOpenChange={(v) => { if (!v) setDlg(d => ({...d, open: false})); }}
        initial={dlg.initial}
        techs={techs}
        onCreated={load}
      />
      <CreateWODialog
        open={dlg.open && dlg.mode === "edit"}
        onOpenChange={(v) => { if (!v) setDlg(d => ({...d, open: false})); }}
        mode="edit"
        wo={dlg.initial}
        techs={techs}
        onSaved={load}
        onDeleted={load}
      />
    </div>
  );
}

function Legend({color, label}) {
  return (
    <span className="inline-flex items-center gap-1.5 text-zinc-700">
      <span className="w-2.5 h-2.5 rounded-sm" style={{background: color}} />
      <span className="font-mono">{label}</span>
    </span>
  );
}

function NowLine() {
  // Convert current instant to WITA wall-clock (UTC+8) for the now-line indicator
  const nowWita = new Date(Date.now() + 8 * 60 * 60 * 1000);
  const hour = nowWita.getUTCHours() + nowWita.getUTCMinutes() / 60;
  if (hour < HOUR_START || hour > HOUR_END) return null;
  const top = (hour - HOUR_START) * ROW_H;
  return (
    <div className="absolute left-0 right-0 pointer-events-none z-30"
         style={{top, height: 1}}>
      <div className="absolute left-0 right-0 h-[2px] bg-red-500/90" />
      <div className="absolute -left-1 w-3 h-3 rounded-full bg-red-500 -translate-y-1/2" />
    </div>
  );
}

const SVC_LABEL_SHORT = {
  AC_REPAIR: "Service AC",
  AC_INSTALL: "Pasang AC",
  CLEANING: "Cleaning",
  CHILLER: "Chiller",
  OTHER: "Layanan",
};

const AI_TINT = "#3B82F6";        // biru untuk event buatan AI
const AI_BORDER = "#1D4ED8";

function isDarkColor(hex) {
  if (!hex) return true;
  const c = hex.replace("#","");
  if (c.length < 6) return true;
  const r = parseInt(c.slice(0,2),16), g = parseInt(c.slice(2,4),16), b = parseInt(c.slice(4,6),16);
  const luma = 0.299*r + 0.587*g + 0.114*b;
  return luma < 140;
}

// v84.96 — Deteksi WO dgn permintaan jam khusus dari customer. Ambil dari
// `description` (mis. "Customer minta jam 10", "layanan minta jam 12").
// Return {hasRequest, timeText} — timeText = "10:00" / "jam 10 pagi" atau "" .
function extractCustomerTimeRequest(desc) {
  if (!desc) return { hasRequest: false, timeText: "" };
  const first = desc.split("\n")[0];
  // Match pola "jam N[:MM]" atau "minta jam" atau HH:MM
  const m = first.match(/\b(?:minta\s*)?jam\s*(\d{1,2})(?:[:.]\s*(\d{2}))?(?:\s*(pagi|siang|sore|malam))?/i)
         || first.match(/\b(\d{1,2}):(\d{2})\b/);
  if (!m && !/minta\s*jam/i.test(first)) return { hasRequest: false, timeText: "" };
  let timeText = "";
  if (m) {
    const h = parseInt(m[1], 10);
    const mm = m[2] ? parseInt(m[2], 10) : 0;
    timeText = `${String(h).padStart(2,"0")}:${String(mm).padStart(2,"0")}`;
    if (m[3]) timeText += " " + m[3].toLowerCase();
  }
  return { hasRequest: true, timeText };
}

// v84.98 — Deteksi permintaan teknisi tertentu dari admin/customer di
// keterangan WO. Contoh: "minta Lukman", "khusus Andi & Risky",
// "request Hendro", "sama Pak Lukman", "kalau bisa lukman".
// v84.99 — SEKARANG dgn validasi: nama hanya valid kalau match daftar teknisi
// asli (`techNameSet` set of lowercase first names). Ini fix false positive
// spt "minta jangan" / "minta ganti" / "minta cepat".
// Return {hasRequest, techName} — techName = "Lukman" / "Andi & Risky" / "" .
function extractRequestedTechnician(desc, techNameSet) {
  if (!desc || !techNameSet || techNameSet.size === 0) {
    return { hasRequest: false, techName: "" };
  }
  const text = desc.replace(/\n/g, " ").slice(0, 240);
  // Trigger words diikuti nama (kapitalisasi opsional). Cari hingga 3 nama
  // terhubung "&" atau ",".
  const re = /(?:\b(?:minta|khusus|request|sama|kalau\s+bisa|utk|untuk)\s+)([A-Za-z]{3,15}(?:\s*[,&]\s*[A-Za-z]{3,15}){0,2})/i;
  const m = text.match(re);
  if (!m) return { hasRequest: false, techName: "" };
  // Split hasil ke tokens, filter yg cocok dgn nama teknisi asli
  const tokens = m[1].split(/[,&\s]+/).map(t => t.trim().toLowerCase()).filter(Boolean);
  const matched = tokens.filter(t => techNameSet.has(t));
  if (matched.length === 0) return { hasRequest: false, techName: "" };
  // Capitalize + join dgn " & "
  const techName = matched
    .map(t => t.charAt(0).toUpperCase() + t.slice(1))
    .join(" & ");
  return { hasRequest: true, techName };
}

function EventCard({ order, tech, colIdx, overlap, techNameSet, onClick, drag, setDrag }) {
  if (!order.scheduled_start || !order.scheduled_end) return null;
  const startH = hoursOf(order.scheduled_start);
  let endH   = hoursOf(order.scheduled_end);
  // v84.53 Bug Fix — WO panjang (mis. 15 unit PASANG) bisa punya scheduled_end
  // di HARI BERIKUTNYA. `hoursOf` cuma parse jam string → endH kecil (mis. 3.75)
  // → filter di bawah `endH <= HOUR_START` return null → event HILANG di kalender.
  // Detect cross-midnight dgn compare tanggal, clamp endH ke HOUR_END biar tetap
  // terlihat (truncated di 21:00, tail-nya visual disembunyikan tanpa lose data).
  const startDate = order.scheduled_start.slice(0, 10);
  const endDate = order.scheduled_end.slice(0, 10);
  const crossedMidnight = endDate > startDate;
  if (crossedMidnight) {
    endH = HOUR_END;   // clamp — event tampak sampai 21:00
  }
  if (endH <= HOUR_START || startH >= HOUR_END) return null;

  const s = Math.max(startH, HOUR_START);
  const e = Math.min(endH, HOUR_END);
  const rowStart = Math.floor(s - HOUR_START) + 1;
  const rowSpan  = Math.max(1, Math.ceil(e - Math.floor(s)));

  // v84.51 — Overlap layout: kalau ada N event bertindih, tiap event dapat
  // width = 100/N% dan digeser ke posisi col-th. Kalau tidak overlap (default),
  // event menutupi full column (left:4 right:4).
  const total = overlap?.total || 1;
  const col = overlap?.col || 0;

  // v84.40 — Prioritas warna: order.color (user pilih) → AI tint → GRAFIT default
  // (dulu #000000 hitam pekat → blend total di dark UI, sulit dibedakan).
  let bg;
  if (order.color && order.color !== "#000000") bg = order.color;
  else if (order.ai_created) bg = AI_TINT;
  else bg = "#616161";   // Grafit — Google Calendar-compatible

  const textColor = isDarkColor(bg) ? "#FFFFFF" : "#111827";
  const isMe = drag && drag.id === order.id;
  const dyPx = isMe ? drag.deltaMin * PX_PER_MIN : 0;
  const innerOffset = (s - Math.floor(s)) * ROW_H + (isMe && drag.mode === "move" ? dyPx : 0);
  const baseHeight = Math.max(28, (e - s) * ROW_H) - 4;
  const innerHeight = baseHeight + (isMe && drag.mode === "resize" ? dyPx : 0);
  // v84.41 — Format judul event mirror Google Calendar: "SERVICE · 3 unit"
  // (bukan lagi "Layanan / 081213483691 · 3 unit"). Prioritas:
  // 1. items multi-layanan → "CLEANING · 2 unit + FREON · 1 unit"
  // 2. service_type single → "SERVICE · 3 unit" (uppercase)
  // 3. legacy title fallback
  let title;
  if (Array.isArray(order.items) && order.items.length) {
    const parts = order.items.map(it => `${(it.service_type || "").toUpperCase()} · ${it.quantity || 1} unit`);
    title = parts.slice(0, 2).join(" + ") + (parts.length > 2 ? ` +${parts.length - 2}` : "");
  } else {
    const svc = (order.service_type || "SERVICE").toUpperCase();
    title = `${svc} · ${order.quantity || 1} unit`;
  }

  function startMove(ev) {
    // Left-click saja, dan bukan pada resize handle (handle stopPropagation)
    if (ev.button !== 0) return;
    ev.preventDefault();
    ev.stopPropagation();
    setDrag({
      id: order.id, mode: "move",
      startY: ev.clientY, startX: ev.clientX, dx: 0,
      techId: tech.id, targetTechId: tech.id, deltaMin: 0,
      origStart: order.scheduled_start, origEnd: order.scheduled_end,
      onSimpleClick: onClick,
    });
  }
  function startResize(ev) {
    if (ev.button !== 0) return;
    ev.preventDefault();
    ev.stopPropagation();
    setDrag({
      id: order.id, mode: "resize",
      startY: ev.clientY, deltaMin: 0,
      origStart: order.scheduled_start, origEnd: order.scheduled_end,
      onSimpleClick: null,   // resize handle bukan area klik
    });
  }

  const badgeText = isMe && drag.deltaMin !== 0
    ? (drag.mode === "move"
        ? `${drag.deltaMin > 0 ? "+" : ""}${drag.deltaMin} mnt`
        : `Durasi ${drag.deltaMin > 0 ? "+" : ""}${drag.deltaMin} mnt`)
    : null;

  return (
    <div style={{ gridColumn: colIdx + 2, gridRow: `${rowStart} / span ${rowSpan}`, position: "relative", pointerEvents: "none", zIndex: 5 + col }}>
      <div
        onMouseDown={startMove}
        className={`rounded-lg px-2 py-1 overflow-hidden cursor-grab active:cursor-grabbing hover:brightness-110 transition-[filter] fade-in shadow-sm ${isMe ? "ring-2 ring-orange-500 z-30 shadow-lg" : ""}`}
        style={{
          position: "absolute",
          top: innerOffset,
          // v84.51 — Kalau overlap N events, tiap event dapat 100/N% width + posisi col-th.
          left: `calc(${(col / total) * 100}% + 2px)`,
          width: `calc(${100 / total}% - 4px)`,
          height: Math.max(24, innerHeight),
          background: bg,
          color: textColor,
          pointerEvents: "auto",
          userSelect: "none",
          transform: isMe && drag.mode === "move" && drag.dx ? `translateX(${drag.dx}px)` : undefined,
          opacity: isMe && drag.targetTechId && drag.targetTechId !== drag.techId ? 0.85 : undefined,
        }}
        title={order.description ? `Ket: ${order.description.split('\n')[0]}` : ""}
        data-testid={`cal-event-${order.id}`}
      >
        {/* v84.51 — Baris 1: waktu MULAI + service value inline (font smaller) */}
        <div className="text-[11px] font-semibold leading-tight truncate flex items-center gap-1">
          {(() => {
            // v84.132 — Quick Order = customer pilih jam spesifik = time-request badge
            const isQO = order.source === "quick_order"
                        || (order.description || "").includes("[via Quick Order Link]");
            const req = extractCustomerTimeRequest(order.description);
            const hasReq = req.hasRequest || isQO;
            let timeText = req.timeText;
            if (!timeText && isQO && order.scheduled_start) {
              // Fallback: ambil dari scheduled_start (Quick Order simpan sesuai slot pilihan)
              timeText = order.scheduled_start.slice(11, 16);   // "HH:MM"
            }
            return hasReq ? (
              <span
                className="inline-flex items-center gap-0.5 bg-amber-400 text-black text-[9px] font-bold rounded-sm px-1 py-[1px] ring-1 ring-amber-600 flex-shrink-0"
                title={isQO
                  ? `Customer pilih jam via Quick Order${timeText ? " " + timeText : ""}`
                  : `Customer minta jam${timeText ? " " + timeText : ""} — ${order.description.split('\n')[0]}`}
                data-testid={`cal-event-time-request-${order.id}`}
              >
                <Clock className="w-2.5 h-2.5" strokeWidth={2.5} />
                {timeText && <span className="font-mono leading-none">{timeText.split(" ")[0]}</span>}
              </span>
            ) : null;
          })()}
          {(() => {
            const tr = extractRequestedTechnician(order.description, techNameSet);
            return tr.hasRequest ? (
              <span
                className="inline-flex items-center gap-0.5 bg-rose-500 text-white text-[9px] font-bold rounded-sm px-1 py-[1px] ring-1 ring-rose-700 flex-shrink-0 uppercase"
                title={`Customer minta teknisi: ${tr.techName} — ${order.description.split('\n')[0]}`}
                data-testid={`cal-event-tech-request-${order.id}`}
              >
                <UserRound className="w-2.5 h-2.5" strokeWidth={2.5} />
                <span className="leading-none">{tr.techName.split(/[,&]/)[0].trim()}</span>
              </span>
            ) : null;
          })()}
          {/* v84.110 — Badge QO (Quick Order) — bermunculan kalau WO source=quick_order */}
          {(order.source === "quick_order" ||
            (order.description || "").includes("[via Quick Order Link]")) && (
            <span
              className="inline-flex items-center bg-blue-500 text-white text-[9px] font-black rounded-sm px-1 py-[1px] ring-1 ring-blue-700 flex-shrink-0 uppercase"
              title="WO dari Quick Order Link customer"
              data-testid={`cal-event-qo-${order.id}`}
            >
              QO
            </span>
          )}
          {/* v84.154 — Badge NO-SHERLOK: balon map merah untuk WO mulai besok+
              yang belum ada maps_link. Admin cepat lihat mana yg butuh follow-up pin.
              Historis (hari ini & sebelumnya) tidak ditandai — tidak actionable. */}
          {(() => {
            if ((order.maps_link || "").trim()) return null;
            const startISO = order.scheduled_start || "";
            if (!startISO) return null;
            const startDate = startISO.slice(0, 10);   // YYYY-MM-DD (WITA-safe)
            // WITA today
            const wita = new Date(Date.now() + 8 * 3600 * 1000);
            const todayStr = wita.toISOString().slice(0, 10);
            if (startDate <= todayStr) return null;   // hari ini / lampau → skip
            return (
              <span
                className="inline-flex items-center bg-red-600 text-white text-[9px] font-black rounded-full p-[2px] ring-1 ring-red-800 flex-shrink-0"
                title="Belum ada link Maps/Sherlok — mohon konfirmasi pin lokasi ke customer"
                data-testid={`cal-event-no-sherlok-${order.id}`}
              >
                <MapPin className="w-2.5 h-2.5" strokeWidth={3} />
              </span>
            );
          })()}
          <span className="font-mono opacity-80">{order.scheduled_start.slice(11,16)}</span>
          <span>·</span>
          <span className="truncate">{title}</span>
          {order.ai_created && (
            <span className="ml-auto text-[8px] bg-white/95 text-[#1D4ED8] font-bold rounded-sm px-1 py-0 flex-shrink-0" title="Dibuat oleh AI — belum direview admin">AI</span>
          )}
        </div>
        {/* Baris 2: alamat customer (font lebih besar, dominan) */}
        {Math.max(24, innerHeight) > 32 && (
          <div className="text-[12px] leading-tight truncate opacity-95 mt-0.5">{order.address || order.customer_name}</div>
        )}
        {/* Baris 3: kalau ada keterangan (Ket: minta jam...) */}
        {Math.max(24, innerHeight) > 52 && order.description && (
          <div className="text-[10px] leading-tight truncate opacity-75 mt-0.5">Ket: {order.description.split('\n')[0]}</div>
        )}
        {badgeText && (
          <div className="absolute top-1 right-1 text-[9px] font-bold bg-black/70 text-white rounded-sm px-1.5 py-0.5"
               data-testid={`cal-drag-badge-${order.id}`}>
            {badgeText}
          </div>
        )}
        {/* v84.53 — indicator kalau event extends past midnight (durasi > sisa hari) */}
        {crossedMidnight && (
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-yellow-400 via-orange-400 to-red-500"
               title={`Event berlanjut ke ${endDate}. End: ${order.scheduled_end.slice(11,16)}`}
               data-testid={`cal-event-overflow-${order.id}`} />
        )}
        {/* Resize handle di bawah */}
        <div
          onMouseDown={startResize}
          className="absolute left-0 right-0 bottom-0 h-1.5 cursor-ns-resize hover:bg-white/40 rounded-b-sm"
          data-testid={`cal-resize-${order.id}`}
        />
      </div>
    </div>
  );
}


// v84.51 — Group events by overlap. Setiap event dapat {col, total} supaya
// bisa dirender side-by-side dgn width proporsional. Algoritma greedy graph
// coloring: sort by start time, assign col ke slot pertama yg free.
function computeOverlapLayout(orders) {
  const evs = (orders || []).filter(o => o.scheduled_start && o.scheduled_end);
  if (evs.length < 2) return new Map();
  const rows = evs.map(o => ({
    id: o.id,
    s: new Date(o.scheduled_start).getTime(),
    e: new Date(o.scheduled_end).getTime(),
  }));
  rows.sort((a, b) => a.s - b.s || a.e - b.e);
  const result = new Map();
  let group = [];
  let groupEnd = -Infinity;
  function flush() {
    if (!group.length) return;
    const cols = [];   // cols[i] = end-time of last event in that column
    for (const r of group) {
      let idx = cols.findIndex(t => t <= r.s);
      if (idx === -1) { idx = cols.length; cols.push(r.e); }
      else cols[idx] = r.e;
      r._col = idx;
    }
    const total = cols.length;
    for (const r of group) {
      if (total > 1) result.set(r.id, { col: r._col, total });
    }
  }
  for (const r of rows) {
    if (r.s >= groupEnd) { flush(); group = []; groupEnd = r.e; }
    else groupEnd = Math.max(groupEnd, r.e);
    group.push(r);
  }
  flush();
  return result;
}
